import {
  OmniElement,
  OmniStyleElement,
  OmniIconElement,
  nothing,
  html,
  css,
  classMap,
} from 'omni-ui';

OmniStyleElement.register();
OmniIconElement.register();

/**
 * @summary Class for PersonaBaseCard
 * @description Base card that wraps Persona textCard, empathyCard, wordCloud and summaryCard.
 * @extends OmniElement
 */
export class PersonaBaseCard extends OmniElement {
  /**
   * @property {Boolean} isEditView - tracks view the card is currently showing
   * @property {Boolean} readOnly - is card readOnly or editable
   * @property {String} cardTitle
   */
  static get properties() {
    return {
      isEditView: { type: Boolean },
      readOnly: { type: Boolean },
      cardTitle: { type: String },
    };
  }

  /**
   * PersonaBaseCard module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        :host,
        omni-style.omni,
        .omni .box {
          position: relative;
          display: flex;
          flex: 1 1 auto;
        }

        .omni .display-view {
          flex-flow: column nowrap;
          position: relative;
          padding: 1.71rem 1.71rem 2.14rem 1.71rem;
          box-shadow:
            0 0.8rem 0.857rem -0.642rem rgba(var(--rgb-nav-two), 0.18),
            0 0.07rem 0.714rem -0.214rem rgba(var(--rgb-nav-two), 0.15);
        }

        .omni .display-view:hover {
          box-shadow:
            0 1.714rem 2.214rem -0.428rem rgba(var(--rgb-nav-two), 0.37),
            0 0.142rem 0.642rem -0.07rem rgba(var(--rgb-nav-two), 0.1);
        }

        .omni .edit-view {
          flex-flow: column nowrap;
          position: absolute;
          width: 100%;
          z-index: 5; /* fighting campaign-audience-insight z-index */
          padding: 2.14rem 2.14rem 2.14rem 2.86rem;
          box-shadow:
            0 1.714rem 2.214rem -0.428rem rgba(var(--rgb-nav-two), 0.37),
            0 0.142rem 0.642rem -0.07rem rgba(var(--rgb-nav-two), 0.1);
        }

        .omni .title-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .omni .title:not(:last-child) {
          margin: 0.29rem 0;
        }

        .omni omni-tooltip button {
          width: 2.28rem;
          height: 2.28rem;
          padding: 0.07rem 0.43rem;
        }

        .display-view omni-tooltip {
          position: absolute;
          right: 1.71rem;
          top: 1.71rem;
          display: none;
        }
        div.display-view.box:hover omni-tooltip {
          display: block;
          z-index: 3;
        }

        .omni ul {
          list-style: disc;
        }

        .omni ul,
        .omni ol {
          padding-left: 1.29rem;
        }

        .button-container {
          display: flex;
          justify-content: flex-end;
        }

        button.is-text {
          margin-right: 0.71rem;
        }
      `,
    ];
  }

  /**
   * add event listerner that invokes {@link #_handleDocumentClick} on click event
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this._handleDocumentClick);
  }

  /**
   * removes event listerner that invokes {@link #_handleDocumentClick} on click event
   */
  disconnectedCallback() {
    document.removeEventListener('click', this._handleDocumentClick);
    super.disconnectedCallback();
  }

  /**
   *
   * @param {click} e - click event
   * @returns undefined
   * @summary - if a click is detected outside of baseCard it invokes {@link #_handleClose}
   */
  _handleDocumentClick = e => {
    const baseCard = this.shadowRoot.querySelector('div.box');
    if (!this.isEditView || e.composedPath().includes(baseCard)) {
      return;
    }

    this._handleClose();
  };

  /**
   * @summary dispatches @event persona-card-close
   */
  _handleClose() {
    this.dispatchNewEvent('persona-card-close');
  }

  /**
   * @summary dispatches @event persona-card-submit
   */
  _handleSubmit() {
    this.dispatchNewEvent('persona-card-submit');
  }

  /**
   * @summary dispatches @event persona-card-edit-click
   */
  _handleEditClick() {
    this.dispatchNewEvent('persona-card-edit-click');
  }

  /**
   *
   * @returns {TemplateResult} main HTML to be rendered on the DOM
   */
  render() {
    return html`
      <omni-style>
        <div
          class=${classMap({
            'display-view': !this.isEditView,
            'edit-view': this.isEditView,
            box: true,
          })}
          part="base-card-container"
        >
          ${!this.isEditView
            ? html`
                <div class="title-container" part="title">
                  ${this.cardTitle?.trim()
                    ? html` <h1 class="title">${this.cardTitle}</h1> `
                    : nothing}
                  ${!this.readOnly
                    ? html`
                        <omni-tooltip part="tooltip">
                          <button
                            @click=${this._handleEditClick}
                            slot="invoker"
                            part="button"
                          >
                            <omni-icon
                              class="is-size-1"
                              icon-id="omni:interactive:edit"
                            >
                            </omni-icon>
                          </button>
                          <div slot="content">Edit</div>
                        </omni-tooltip>
                      `
                    : nothing}
                </div>
              `
            : nothing}

          <slot></slot>

          ${this.isEditView
            ? html`
                <div class="button-container" part="buttons">
                  <button class="button is-text" @click=${this._handleClose}>
                    Cancel
                  </button>
                  <button
                    class="button is-primary"
                    @click=${this._handleSubmit}
                  >
                    Update
                  </button>
                </div>
              `
            : nothing}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('persona-base-card', PersonaBaseCard);
